import { t } from '@ctcells/i18n'
import { useCallback, useId, useLayoutEffect, useRef, useState, type CSSProperties } from 'react'

import { Button } from '../atoms/Button.tsx'
import { cx } from '../atoms/cx.ts'
import { useDisabled } from '../atoms/disabled.tsx'
import { DialogSurface } from '../atoms/DialogSurface.tsx'
import { Icon } from '../atoms/Icon.tsx'

import type { StripBatchChoice } from './batchStripTypes.ts'
import { useOutsideClick } from './useOutsideClick.ts'

import '../atoms/menu.css'

/**
 * 작업 줄 [작업 선택] **그 자리 드롭다운**(eric 2026-10-22 「작업 선택을 클릭하면.. 작업 메뉴로 가지 말고.. 작업 아이디 혹은
 * 작업 이름이 뜨게 해줘..」).
 *
 * - 값을 고르는 것이라 `listbox` 다(`SortPill` 과 같은 패턴 — 고른 값이 체크로 남는다). 행동 메뉴(`RowMenu`)가 아니다.
 * - 한 줄 = 작업 이름(서버 `Batch.name`) 주 글자 + 작업 ID 앞 8자리 · 상태 보조 글자. 지금 띄운 작업에 체크(✓)가 선다.
 * - 화살표 · Enter · Space 로 고르고 Esc · 바깥 누름으로 닫는다. 초점은 여는 단추에 남고 `aria-activedescendant` 가 가리킨다.
 * - 제목은 단추 이름과 같은 「작업 선택」(eric 2026-10-29). 고를 작업이 없으면 「대기 작업 없음」 한 줄.
 * - [작업 추가](셸이 길을 줄 때만)는 목록 유무와 무관하게 구분선 아래 늘 선다(eric 2026-10-29).
 * 🔴 **새 조회가 없다** — 목록은 작업 줄이 이미 받은 활성 작업 캐시(`useActiveBatchSource`)에서 거른 것이다(DP-01).
 * 🔴 **서버에 쓰지 않는다** — 고른 값은 클라 상태(`@ctcells/core` `useStripChoiceStore`)다. 그래서 권한 · 제어락 · 연결과 무관하다.
 *    막히는 것은 하나 — 시작한 작업이 줄을 쥐고 있을 때(비활성 + 사유 · 숨기지 않는다).
 */
export function StripBatchPicker({
  options,
  currentId,
  onChoose,
  onAddBatch,
  addDisabledReason,
  disabled,
  disabledReason,
}: StripBatchChoice) {
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const anchorRef = useRef<HTMLDivElement>(null)
  const listId = useId()
  const { isDisabled, ariaProps, reasonNode } = useDisabled(
    disabled,
    disabled === true ? disabledReason : undefined,
  )
  const close = useCallback(() => setOpen(false), [])
  useOutsideClick(anchorRef, open, close)

  /*
   * 🔴 **펼친 목록은 화면 기준(`position: fixed`)이다** — 작업 줄의 칸들은 높이 불변을 위해 `overflow: hidden` 이라(`.strip > *`)
   *    앵커 기준 절대 배치면 1행 아래로 잘린다. 자리는 단추를 재서 넣는다(`Combobox` 와 같은 방식).
   */
  const [place, setPlace] = useState<CSSProperties | undefined>(undefined)
  useLayoutEffect(() => {
    if (!open) return undefined
    const measure = () => {
      const anchor = anchorRef.current?.getBoundingClientRect()
      if (anchor === undefined) return
      setPlace({ left: anchor.left, top: anchor.bottom + 4 })
    }
    measure()
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [open])

  const optionId = (index: number) => `${listId}-${String(index)}`
  const empty = options.length === 0

  const openMenu = () => {
    const current = options.findIndex((option) => option.id === currentId)
    setActiveIndex(current < 0 ? 0 : current)
    setOpen(true)
  }

  const choose = (id: string) => {
    setOpen(false)
    if (id !== currentId) onChoose(id)
  }

  /*
   * [작업 추가]는 대기 작업이 있든 없든 목록 아래 늘 선다(eric 2026-10-29 「작업 추가는 작업이 있어도 보이게」).
   * 작은 기본(테두리) 단추다 — 드롭다운의 주 동작은 «고르기»라 강조색을 쓰지 않는다.
   */
  const addButton =
    onAddBatch === undefined ? undefined : (
      <Button
        size="sm"
        {...(addDisabledReason === undefined
          ? {
              onClick: () => {
                setOpen(false)
                onAddBatch()
              },
            }
          : { disabled: true as const, disabledReason: addDisabledReason })}
      >
        {t('action.newBatch')}
      </Button>
    )

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (isDisabled) return
    if (event.key === 'Escape') {
      if (open) event.preventDefault()
      setOpen(false)
      return
    }
    if (!open) {
      if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        openMenu()
      }
      return
    }
    if (empty) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex((index) => (index + 1) % options.length)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((index) => (index - 1 + options.length) % options.length)
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      const option = options[activeIndex]
      if (option !== undefined) choose(option.id)
    }
  }

  return (
    <div className="a-menu-anchor strip__picker" ref={anchorRef}>
      <button
        aria-activedescendant={open && !empty ? optionId(activeIndex) : undefined}
        aria-controls={open ? listId : undefined}
        aria-expanded={open}
        aria-haspopup="listbox"
        className="a-btn a-btn--pill"
        onClick={isDisabled ? undefined : () => (open ? setOpen(false) : openMenu())}
        onKeyDown={onKeyDown}
        type="button"
        {...ariaProps}
      >
        <Icon name="list" size="sm" />
        {t('action.selectBatch')}
        {reasonNode}
      </button>

      {open ? (
        <DialogSurface
          title={t('action.selectBatch')}
          onClose={close}
          className="strip__picker-menu"
          id={empty ? listId : undefined}
          style={place}
          footer={addButton}
        >
          {empty ? (
            <p className="strip__picker-empty">{t('strip.picker.empty')}</p>
          ) : (
            <ul
              aria-label={t('action.selectBatch')}
              className="plainlist"
              id={listId}
              role="listbox"
            >
              {options.map((option, index) => {
                const chosen = option.id === currentId
                return (
                  <li
                    aria-selected={chosen}
                    className={cx('a-menu__opt', index === activeIndex && 'a-menu__opt--active')}
                    id={optionId(index)}
                    key={option.id}
                    onClick={() => choose(option.id)}
                    onPointerEnter={() => setActiveIndex(index)}
                    role="option"
                  >
                    <span className="strip__picker-text">
                      <span className="strip__picker-name" title={option.name}>
                        {option.name}
                      </span>
                      <span className="strip__picker-sub">
                        <span className="mono">{option.shortId}</span>
                        {option.sub === undefined ? null : ` · ${option.sub}`}
                      </span>
                    </span>
                    {chosen ? (
                      <span aria-hidden="true" className="a-menu__check">
                        ✓
                      </span>
                    ) : null}
                  </li>
                )
              })}
            </ul>
          )}
        </DialogSurface>
      ) : null}
    </div>
  )
}
